import { AnimatePresence, motion } from "framer-motion";
import { ElementType, FC, MutableRefObject, useRef } from "react";

import __ from "web/Layout/Translations";

import { PropsWithClasses } from "web/types/Common";
import type { Nullable } from "web/types/Utils";

import classify from "web/classify";

import useModalFocus from "../utils/useModalFocus";
import defaultStyles from "./modal.scss";

const [initial, animate, exit] = [
  { opacity: 0, y: 200 },
  { opacity: 1, y: 0 },
  { opacity: 0, y: -200 },
];

interface IMobileModalProps {
  component?: ElementType;
  closeAction?: () => void;
  modalRef: MutableRefObject<Nullable<HTMLDivElement>>;
  ariaLabel?: Nullable<string>;
  hideCloseButton?: Nullable<boolean>;
  persistent?: Nullable<boolean>;
}

const MobileModal: FC<PropsWithClasses<IMobileModalProps>> = ({
  classes = {},
  component: Component = null,
  closeAction = () => {},
  modalRef,
  ariaLabel,
  hideCloseButton = false,
  persistent = false,
}) => {
  const title = __("Zamknąć");

  const contentRef = useRef<HTMLDivElement>(null);

  const { handleKeyDown } = useModalFocus({ component: Component, modalRef });

  return Component ? (
    <AnimatePresence>
      <motion.div
        className={classes.root}
        initial={initial}
        animate={animate}
        exit={exit}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        ref={modalRef}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel || undefined}
      >
        <div ref={contentRef}>
          {typeof Component === "function" ? (
            <Component closeAction={closeAction} />
          ) : (
            Component
          )}
          {!persistent && !hideCloseButton && (
            <button
              type="button"
              className={classes.closeButton}
              onClick={closeAction}
              title={title}
              aria-label={__("Zamknij modal")}
            >
              <span className="visually-hidden">{title}</span>
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  ) : null;
};

export default classify<PropsWithClasses<IMobileModalProps>>(defaultStyles)(
  MobileModal
);
